import { useEffect } from 'react';
import { useUnmount } from 'react-use';

import { AppointmentId } from '@dreryk/egabinet-appointment-published-language-api';
import { VisitSettlement, VisitSettlementQuery } from '@dreryk/egabinet-financial-settlement-frontend-api';
import { PurchaseId } from '@dreryk/egabinet-financial-settlement-published-language-api';

import { useCqrsSearch } from '@dreryk/command-query-react-common';
import { paginations } from '@dreryk/command-query-ws-client';
import { dispatchSnackbarNegativeEvent } from '@dreryk/react-components';

import { ObjectMap } from '~libs.typescript';

import { prescriptionVisitsTranslationKeys } from '../../../translations';

import { usePrescriptionVisitSettlementGlobalState } from './usePrescriptionVisitSettlementGlobalState';

const DEFAULT_PAGINATION = paginations.all();

type PrescriptionVisitSettlementResolverProps = {
   readonly appointmentIds: number[];
};

export const PrescriptionVisitSettlementResolver = (props: PrescriptionVisitSettlementResolverProps): null => {
   const { updateSettlement, resetSettlement } = usePrescriptionVisitSettlementGlobalState();

   const { data: visitSettlements } = useCqrsSearch<VisitSettlement>(
      () => {
         const query = new VisitSettlementQuery();
         query.purchaseIds = props.appointmentIds.map((appointmentId) =>
            PurchaseId.forAppointment(AppointmentId.of(appointmentId))
         );
         return query;
      },
      DEFAULT_PAGINATION,
      {
         onUnexpectedError: () => {
            dispatchSnackbarNegativeEvent(prescriptionVisitsTranslationKeys.settlementServiceUnavailable);
         },
      }
   );

   useEffect(() => {
      if (!visitSettlements) {
         return;
      }

      const settlementByPurchaseId = new ObjectMap<PurchaseId, VisitSettlement>(
         visitSettlements.map((settlement) => [settlement.purchaseId, settlement])
      );
      const resultMap: Map<number, VisitSettlement> = new Map();

      props.appointmentIds.forEach((appointmentId) => {
         const settlement = settlementByPurchaseId.get(PurchaseId.forAppointment(AppointmentId.of(appointmentId)));
         if (settlement) {
            resultMap.set(appointmentId, settlement);
         }
      });

      updateSettlement(resultMap);
   }, [visitSettlements, props.appointmentIds, updateSettlement]);

   useUnmount(resetSettlement);

   return null;
};
